import { claude, skill } from '../api.js';
import { $, briefTxt, esc, show, showErr } from '../dom.js';
import { createPage } from '../logic/pages.ts';
import { IMAGE_QUERIES_SCHEMA } from '../prompts/constants.js';
import { buildMarketingBriefContext, syncFlatTokens } from '../prompts/context.js';
import { INSPIRATION_BLOCK, SKILLS } from '../prompts/index.js';
import { renderBrandPanel, setRegenHandler } from '../screens/concept.js';
import { hasLogo, ST, sectionId } from '../state.js';
import { getLogoVisionInput, initSection, newPage } from './helpers.js';
import { searchStockImages } from './stock-images.js';

// ── CONCEPT GENERATION ────────────────────────────────────────────
// Loading UI, concept generation from brief, page/section setup.

let LdInterval = null;

// ── LOADING UI (DOM-dependent) ── Move to Vue component during migration.

// Steps that map to MARKETING_BRIEF_SCHEMA fields — driven by onChunk streaming.
// Keys are chosen to be EVENLY SPACED through the JSON output order (see schema field order in constants.js).
const ANALYSIS_STEPS_EXISTING = [
  'Analyzing your website',
  'Extracting colors & fonts',
  'Learning your tone of voice',
  'Gathering facts & credentials',
  'Identifying conversion signals',
  'Finalizing brand profile',
];
// Schema output order:
// companyName→tagline→overview→industry→colors→fonts→aesthetic→tone→values→logo→facts→audience→problem→differentiator→ctas→proofs→objections→social→copyright→video→nav
const ANALYSIS_KEYS = [
  '"companyName":',
  '"fonts":',
  '"toneOfVoice":',
  '"facts":',
  '"proofPoints":',
  '"navLinks":',
];
const ANALYSIS_STEPS_DOCS = [
  'Reading your documents',
  'Extracting colors & fonts',
  'Summarizing your business',
  'Identifying brand voice',
  'Gathering facts',
  'Determining brand direction',
];
const ANALYSIS_STEPS_QA = [
  'Analyzing your answers',
  'Extracting colors & fonts',
  'Summarizing your business',
  'Identifying brand voice',
  'Gathering facts',
  'Building your profile',
];

function startLoadingSteps(title, steps, preview) {
  // Update title + clear stale sub-text from previous loading phases
  const titleEl = $('ld-dna-title');
  if (titleEl) {
    titleEl.textContent = title;
  }
  const subEl = $('ld-dna-sub');
  if (subEl) {
    subEl.textContent = '';
  }

  // Show preview card in loading state — actual image set by screenshot fetch callback in screens.js
  const card = $('ld-preview-card');
  const img = $('ld-preview-img');
  if (card && preview?.url) {
    card.classList.add('loading');
    card.style.display = 'block';
    if (img) {
      img.style.display = 'none';
    }
  } else if (card) {
    card.style.display = 'none';
  }

  // Show URL label
  const urlWrap = $('ld-url');
  const urlText = $('ld-url-text');
  if (urlWrap && urlText && preview?.url) {
    urlText.textContent = preview.url;
    urlWrap.style.display = 'inline-flex';
  } else if (urlWrap) {
    urlWrap.style.display = 'none';
  }

  // Render all chips at once — first one active, rest pending
  const chipsEl = $('ld-chips');
  if (chipsEl) {
    chipsEl.innerHTML = steps
      .map(
        (s, i) =>
          `<span class="ld-chip${i === 0 ? ' active' : ''}" id="ld-chip-${i}"><span class="ld-chip-dot"></span>${s}</span>`,
      )
      .join('');
  }

  // Chips are now advanced by streaming callbacks (onChunk/onThinking in genConcept)
  // For non-concept loading screens (existing analysis, doc analysis), use a timer fallback
  if (LdInterval) {
    clearInterval(LdInterval);
    LdInterval = null;
  }
}

// Timer-based chip progression — used by analysis flows that don't have streaming progress
function startLoadingStepsWithTimer(title, steps, preview) {
  startLoadingSteps(title, steps, preview);
  let idx = 0;
  LdInterval = setInterval(() => {
    const cur = $(`ld-chip-${idx}`);
    if (cur) {
      cur.classList.remove('active');
      cur.classList.add('done');
    }
    idx++;
    if (idx >= steps.length) {
      clearInterval(LdInterval);
      LdInterval = null;
      return;
    }
    const next = $(`ld-chip-${idx}`);
    if (next) {
      next.classList.add('active');
    }
  }, 3000);
}

// Create an onChunk callback that advances loading chips based on JSON field detection
function makeAnalysisChunkHandler(keys) {
  let lastStep = -1;
  return text => {
    // Find the highest matching key
    let target = lastStep;
    for (let i = 0; i < keys.length; i++) {
      if (i > lastStep && text.includes(keys[i])) {
        target = i;
      }
    }
    if (target <= lastStep) {
      return;
    }
    // Clear all active, mark previous as done, set target as active
    for (let i = 0; i <= target; i++) {
      const chip = $(`ld-chip-${i}`);
      if (!chip) {
        continue;
      }
      if (i < target) {
        chip.classList.remove('active');
        chip.classList.add('done');
      } else {
        chip.classList.remove('done');
        chip.classList.add('active');
      }
    }
    lastStep = target;
  };
}

function stopLoadingSteps() {
  if (LdInterval) {
    clearInterval(LdInterval);
    LdInterval = null;
  }
  const img = $('ld-preview-img');
  if (img) {
    img.src = '';
  }
  const card = $('ld-preview-card');
  if (card) {
    card.classList.remove('loading');
  }
  const thinkWrap = $('ld-thinking');
  if (thinkWrap) {
    thinkWrap.style.display = 'none';
  }
}

// ── CONCEPT GENERATION (mixed: API + DOM) ── Extract API logic to composable, loading UI to component.

// Store enriched context so section builder can use it regardless of onboarding path
let ConceptBusy = false;

/**
 * Generate a full site concept from brief, logo, and optional inspiration.
 * @param {string} [note]
 * @param {import('../types.ts').UploadedImage | null} [inspo]
 * @param {import('../types.ts').UploadedImage[]} [extraImgs]
 * @returns {Promise<void>}
 */
async function genConcept(note = '', inspo = null, extraImgs = []) {
  if (ConceptBusy) {
    return;
  }
  if (!hasLogo()) {
    showErr('Logo is required. Please upload a logo or provide a logo URL.');
    return;
  }
  ConceptBusy = true;
  G.go('loading');
  const ConceptSteps = [
    { key: 'thinking', label: 'Crafting your concept' },
    { key: 'designVision', label: 'Creating design vision' },
    { key: 'designTokens', label: 'Selecting colors & typography' },
    { key: 'heroHeadline', label: 'Writing headlines & copy' },
    { key: 'sections', label: 'Planning page structure' },
    { key: 'strategy', label: 'Finalizing conversion strategy' },
  ];
  startLoadingSteps(
    'Crafting brand concept\u2026',
    ConceptSteps.map(s => s.label),
  );
  // No timeout guard — streaming keeps the connection alive. Errors surface naturally via catch.
  try {
    const imgs = [];
    const logoVision = getLogoVisionInput(
      'Company logo \u2014 analyze colors, style, and composition for brand-aligned design',
    );
    if (logoVision) {
      imgs.push(logoVision);
    }
    if (inspo) {
      imgs.push({
        ...inspo,
        label:
          'Inspiration website — study the design quality bar and visual energy. Adapt the feeling, do not copy the layout.',
      });
    }
    if (extraImgs?.length) {
      imgs.push(
        ...extraImgs.map(i => ({ ...i, label: `User-uploaded brand asset: ${i.name || 'image'}` })),
      );
    }
    // Pass existing website screenshot so concept AI can see the current design
    if (ST.siteScreenshot?.length) {
      imgs.push(
        ...ST.siteScreenshot.map((tile, i) => ({
          ...tile,
          label:
            i === 0
              ? 'Full-page screenshot of the current website — ' +
              (ST.existingOpt === 'keep'
                ? 'preserve this layout, elevate visual quality'
                : ST.existingOpt === 'rebrand'
                  ? 'this is what you are moving AWAY from'
                  : 'understand the existing visual identity, then elevate it')
              : `(screenshot tile ${i + 1})`,
        })),
      );
    }
    const sk = skill('concept');
    const sys = sk.prompt || '';
    const packs = SKILLS.packs.filter(p => p.active).map(p => p.name);
    const fullSys =
      sys +
      (inspo ? `\n\n${INSPIRATION_BLOCK}` : '') +
      (packs.length
        ? '\n\nActive industry packs: ' +
        packs.join(', ') +
        ' — weight section selection toward most relevant.'
        : '');
    const briefContext = ST.marketingBrief
      ? buildMarketingBriefContext(ST.marketingBrief)
      : briefTxt();
    const screenshotNote = ST.siteScreenshot?.length
      ? ST.existingOpt === 'keep'
        ? '\n\n<screenshot_context>A screenshot of the current website is provided. Study the existing layout, spacing, imagery, and visual patterns. Your designVision should elevate the visual quality while preserving the recognizable structure. The screenshot shows what the visitor currently sees — your output should feel like a premium redesign of the same page, not a different website.</screenshot_context>'
        : ST.existingOpt === 'rebrand'
          ? '\n\n<screenshot_context>A screenshot of the current website is provided. This is what you are moving AWAY from. Study it to understand what to change, then create a distinctly different designVision that matches the new brand direction.</screenshot_context>'
          : '\n\n<screenshot_context>A screenshot of the current website is provided. Use it to understand the existing visual identity, layout patterns, and aesthetic — then create a designVision that elevates the design while respecting the brand.</screenshot_context>'
      : '';
    const userText =
      [briefContext, note, screenshotNote].filter(Boolean).join('\n\n') ||
      '(No brief provided — generate a generic professional site)';

    // Send uploaded docs as proper document content blocks (longform data before instructions)
    const conceptDocs = [];
    if (ST.uploads?.docs?.length) {
      for (const d of ST.uploads.docs) {
        if (d.status === 'done' && d.markdown) {
          conceptDocs.push({
            data: d.markdown,
            title: d.name,
            mediaType: 'text/plain',
            context:
              'Uploaded business document — extract all facts, names, services, and details for the website concept.',
          });
        }
      }
    }
    // Track which JSON fields have been generated for real-time progress
    let LastStep = -1;
    const AdvanceTo = stepIdx => {
      // Clear ALL active chips first — ensures only ONE is ever active
      for (let i = 0; i <= stepIdx; i++) {
        const chip = $(`ld-chip-${i}`);
        if (!chip) {
          continue;
        }
        if (i < stepIdx) {
          chip.classList.remove('active');
          chip.classList.add('done');
        } else {
          chip.classList.remove('done');
          chip.classList.add('active');
        }
      }
      LastStep = stepIdx;
    };

    const raw = await claude({
      sys: fullSys,
      user: userText,
      docs: conceptDocs,
      imgs,
      tokens: sk.tokens || 2600,
      model: sk.model,
      temp: sk.temp,
      effort: sk.effort,
      thinking: sk.thinking,
      schema: sk.schema,
      onThinking: (() => {
        let rendered = 0; // chars already rendered
        let timerId = null;
        return ev => {
          const titleEl = $('ld-dna-title');
          const thinkWrap = $('ld-thinking');
          const scroll = $('ld-thinking-scroll');
          if (ev.active) {
            if (LastStep < 0) {
              AdvanceTo(0);
            }
            if (titleEl) {
              titleEl.textContent = 'Thinking about your brand\u2026';
            }
            if (thinkWrap) {
              thinkWrap.style.display = '';
            }
            // Typewriter: progressively reveal thinking text with natural pacing
            if (scroll && ev.text) {
              const fullText = ev.text;
              const typewrite = () => {
                timerId = null;
                if (rendered >= fullText.length) {
                  return;
                }
                // Fast streaming with micro-pauses at sentence boundaries
                const char = fullText[rendered];
                const isSentenceEnd = char === '.' || char === '!' || char === '?' || char === '\n';
                const isClause = char === ',' || char === ';' || char === '\u2014';
                const chunkSize = isSentenceEnd ? 1 : isClause ? 2 : 3 + Math.floor(Math.random() * 2);

                const end = Math.min(rendered + chunkSize, fullText.length);
                const visible = fullText.slice(0, end);
                const sentences = visible.split(/(?<=[.!?\n])\s+/);
                scroll.innerHTML = sentences
                  .map((sentence, index) =>
                    index === sentences.length - 1
                      ? `<span class="ld-tw-line"><span class="ld-tw-cursor">${esc(sentence)}</span></span>`
                      : `<span class="ld-tw-line">${esc(sentence)}</span>`,
                  )
                  .join('');
                rendered = end;
                const isAtBottom = thinkWrap.scrollHeight - thinkWrap.scrollTop - thinkWrap.clientHeight < 40;
                if (isAtBottom) {
                  thinkWrap.scrollTop = thinkWrap.scrollHeight;
                }
                if (rendered < fullText.length) {
                  const delay = isSentenceEnd ? 60 : 16;
                  timerId = setTimeout(typewrite, delay);
                }
              };
              if (!timerId) {
                timerId = setTimeout(typewrite, 12);
              }
            }
          } else {
            // Thinking done
            if (timerId) {
              clearTimeout(timerId);
              timerId = null;
            }
            const chip = $('ld-chip-0');
            if (chip) {
              chip.classList.remove('active');
              chip.classList.add('done');
            }
            if (titleEl) {
              titleEl.textContent = 'Generating concept\u2026';
            }
            if (thinkWrap) {
              thinkWrap.style.display = 'none';
            }
          }
        };
      })(),
      onChunk(text) {
        // First text chunk = thinking is done (or was skipped), mark thinking chip done
        if (LastStep < 1) {
          const chip0 = $('ld-chip-0');
          if (chip0) {
            chip0.classList.remove('active');
            chip0.classList.add('done');
          }
          const sub = $('ld-dna-title');
          if (sub) {
            sub.textContent = 'Generating concept\u2026';
          }
        }
        // Detect which JSON fields have appeared in the streaming output
        // Uses key+":" to reduce false matches inside string values
        if (text.includes('"designVision":') && LastStep < 1) {
          AdvanceTo(1);
        }
        if (text.includes('"designTokens":') && LastStep < 2) {
          AdvanceTo(2);
        }
        if (text.includes('"heroHeadline":') && LastStep < 3) {
          AdvanceTo(3);
        }
        if (text.includes('"sections":') && LastStep < 4) {
          AdvanceTo(4);
        }
        if (text.includes('"strategy":') && LastStep < 5) {
          AdvanceTo(5);
        }
      },
    });
    if (!ConceptBusy) {
      return;
    }
    let c;
    try {
      c = JSON.parse(raw);
    } catch (parseErr) {
      throw new Error('Concept JSON was truncated — try again or simplify your brief');
    }
    syncFlatTokens(c);
    ST.concept = c;
    ST.originalConceptSections = structuredClone(c.sections || []);
    // Clear cached build artifacts from previous concept
    ST.cachedTokenCtx = null;
    ST.cachedBusinessCtx = null;
    ST.cachedSkillCtx = {};
    ST.conceptContext = userText;

    const homeSects = (c.sections || []).map(s => initSection(s));
    const homePage = { ...newPage('Home', homeSects), id: 'page-home' };

    if (ST.pages.length > 1) {
      // Preserve non-home pages on concept regeneration
      ST.pages[0] = homePage;
      ST.activePage = 0;
    } else {
      ST.pages = [homePage];
    }

    // Create tabs for selected discovered pages
    const selectedPages = (ST.discoveredPages || []).filter(p => p.selected);
    for (const discovered of selectedPages) {
      // Don't add if a page with this sourceUrl already exists
      if (ST.pages.some(p => p.sourceUrl === discovered.url)) {
        continue;
      }
      const newPageObj = createPage(discovered.name, ST.pages, sectionId);
      if (newPageObj) {
        newPageObj.sourceUrl = discovered.url;
        newPageObj.pageMode = undefined;
        newPageObj.analyzed = false;
        ST.pages.push(newPageObj);
      }
    }

    ST.activePage = 0;
    ST.inspoImg = null;
    ST.homePageScreenshot = null;
    // Auto-search for relevant stock images (skip only for existing-site keep mode — images come from siteStructure)
    if (ST.existingOpt === 'keep' && ST.startPath === 'existing') {
      ST.stockImages = [];
      ST.stockPromise = null;
    } else {
      // Haiku image queries + stock search run in background — don't block UI
      ST.stockPromise = (async () => {
        try {
          const queryResult = await claude({
            sys: 'Generate Unsplash search queries for a website based on the design vision and sections.',
            user: `<design_vision>${JSON.stringify(c.designVision)}</design_vision>\n<sections>${c.sections
              .map(s => `${s.type}: ${s.visualBrief || s.title}.`)
              .join('\n')}</sections>`,
            model: 'claude-haiku-4-5',
            tokens: 300,
            schema: IMAGE_QUERIES_SCHEMA,
          });
          c.imageSearchQueries = JSON.parse(queryResult).queries || [];
        } catch (_e) {
          c.imageSearchQueries = [];
        }
        await searchStockImages(c);
        renderBrandPanel();
      })().catch(() => {
        /* ignore */
      });
    }
    stopLoadingSteps();
    ConceptBusy = false;
    G.genDesignPreview();
  } catch (e) {
    stopLoadingSteps();
    ConceptBusy = false;
    console.error('Concept generation failed:', e);
    showErr(e.message);
    // Navigate back properly — use G.go() so render functions are called
    const safePrev = ['brief', 'existing', 'upload', 'start', 'brief-review', 'ex-detail'].includes(
      ST.prev,
    )
      ? ST.prev
      : 'start';
    if (!ST.apiKey) {
      show('api-modal');
    }
    G.go(safePrev);
  }
}

// Register genConcept so brand panel regen button can call it (avoids circular import)
setRegenHandler(() => genConcept());

// ── MODULE EXPORTS ──────
export {
  ANALYSIS_KEYS,
  ANALYSIS_STEPS_DOCS,
  ANALYSIS_STEPS_EXISTING,
  ANALYSIS_STEPS_QA,
  genConcept,
  makeAnalysisChunkHandler,
  startLoadingSteps,
  startLoadingStepsWithTimer,
  stopLoadingSteps,
};
